import type React from 'react'
import { t } from './i18n.ts'
export type Role = 'sindico' | 'subsindico' | 'conselheiro' | 'administradora' | 'condomino' | 'morador' | 'funcionario'
export type Visibility = 'todos' | 'conselho' | 'sindico'

export interface Me {
  user: { id: string; name: string; phone: string; locale: 'pt-BR' | 'en'; whatsappOptIn: boolean; whatsappAssistant: boolean; whatsappAssistantEnabled: boolean; isPlatformAdmin: boolean; email: string | null; emailVerified: boolean; emailOptIn: boolean; emailPending: string | null; emailBounced: boolean; emailChannel: boolean; pushChannel: boolean } | null
  memberships: { condoId: string; slug: string; name: string; role: Role; roleLabel: string; membershipId: string }[]
}

export interface CondoInfo {
  condo: {
    id: string; slug: string; name: string; cnpj: string | null; address: string | null; city: string | null; state: string | null
    timezone: string; plan: string; externalAiProcessing: boolean; convencaoVedaEletronica: boolean
    voteWeight: 'unidade' | 'fracao_ideal'; emergencyContacts: { label: string; phone: string }[]
    brand: CondoBrand
    chat: { channelEnabled: boolean; retentionDays: number; assistantOptIn: boolean }
  }
  me: { membershipId: string; role: Role; roleLabel: string; capabilities: string[] }
}

/** White label (ADR 0025): what the condo shows (own values first, then the administradora's) and its own values. */
export interface CondoBrand {
  name: string | null; color: string | null; hasLogo: boolean; logoSource: 'condo' | 'partner' | null; partnerName: string | null
  own: { brandName: string | null; brandColor: string | null; hasLogo: boolean }
}

/** CSS variables that recolour the primary tokens for a brand colour (hover and tint derived with color-mix). */
export function brandStyle(color: string | null | undefined): React.CSSProperties | undefined {
  if (!color) return undefined
  return { '--color-caneta': color, '--color-caneta-escura': `color-mix(in srgb, ${color} 78%, black)`, '--color-caneta-clara': `color-mix(in srgb, ${color} 12%, white)` } as React.CSSProperties
}

export interface Citation {
  n: number; chunkId: string; documentId: string; versionId: string | null; title: string; category: string
  headingPath: string; pageFrom: number | null; pageTo: number | null; snippet: string
}

export interface DocRow {
  id: string; title: string; category: string; categoryLabel: string; visibility: Visibility; docDate: string | null
  folderId: string | null; createdAt: string; updatedAt: string; createdBy: string | null
  current: { id: string; version: number; status: string; pages: number | null; immutable: boolean; kind: string; signature: string | null; mime: string; size: number } | null
}

export const CATEGORY_OPTIONS: [string, string][] = [
  ['convencao', t('Convenção')], ['regimento', t('Regimento interno')], ['ata', t('Ata')], ['edital', t('Edital')],
  ['contrato', t('Contrato')], ['orcamento', t('Orçamento')], ['nota_fiscal', t('Nota fiscal')], ['balancete', t('Balancete')],
  ['laudo', t('Laudo')], ['avcb', 'AVCB'], ['apolice', t('Apólice de seguro')], ['foto', t('Foto')], ['comunicado', t('Comunicado')], ['outro', t('Outro')],
]
export const VISIBILITY_LABEL: Record<Visibility, string> = { todos: t('Todos os moradores'), conselho: t('Síndico e conselho'), sindico: t('Só síndico e subsíndico') }
export const ROLE_OPTIONS: [Role, string][] = [
  ['condomino', t('Condômino(a) (proprietário)')], ['morador', t('Morador(a) / inquilino(a)')], ['conselheiro', t('Conselheiro(a)')],
  ['subsindico', t('Subsíndico(a)')], ['sindico', t('Síndico(a)')], ['administradora', t('Administradora')], ['funcionario', t('Funcionário(a)')],
]
